import {
  ChangeDetectionStrategy,
  ChangeDetectorRef,
  Component,
  EventEmitter,
  forwardRef,
  Input,
  OnChanges,
  OnDestroy,
  OnInit,
  Output,
  SimpleChanges,
} from '@angular/core';
import { ControlValueAccessor, FormControl, NG_VALUE_ACCESSOR } from '@angular/forms';
import { Element } from '../../helper/element';
import { Subject } from 'rxjs';
import { distinctUntilChanged, filter, takeUntil } from 'rxjs/operators';
import { ClassListCtor, mixinClassList } from '../../common-behaviors/classList';

class InputBase {}

const _InputMixinBase: ClassListCtor & typeof InputBase = mixinClassList(InputBase);

@Component({
  selector: 'app-input',
  templateUrl: './input.component.html',
  styleUrls: ['./input.component.scss'],
  providers: [
    {
      provide: NG_VALUE_ACCESSOR,
      useExisting: forwardRef(() => InputComponent),
      multi: true,
    },
  ],
  changeDetection: ChangeDetectionStrategy.OnPush,
})
export class InputComponent extends _InputMixinBase implements OnInit, OnChanges, OnDestroy, ControlValueAccessor {
  @Input() id = '';
  @Input() override label = '';
  @Input() tooltip = '';
  @Input() required = false;
  @Input() disabled = false;
  @Input() formControl: FormControl = new FormControl();
  @Input() errorMessages = {};
  @Input() private = false;
  @Input() precision = 2;
  @Input() minPrecision?: number;
  @Input() numeric: 'float' | 'number' | 'digitalString' | 'money' | 'decimal' | 'decimalString' | false = false;
  @Input() cutSymbols: string | Array<string> = [];
  @Input() shouldConvertToNegative = false;
  @Input() forbidZeroValue = false;
  @Input() textZeroValue = false;
  @Input() placeholder = '';
  @Input() wrap = true;
  @Input() readonly = false;
  @Input() name = '';
  @Input() borderColor = '';
  @Input() borderRadius = '';
  @Input() colorLabel: 'black-label' | 'bold-grey-label' = 'bold-grey-label';
  @Input() error?: string; // для вывода произвольной ошибки
  @Input() clearable = true; // выводить ли крестик очистки поля в инпуте
  @Input() maxLength: number | string = 524288;
  @Input() autocomplete = 'on';
  @Input() icon: 'search' | 'money' | '' = '';
  @Input() showIcon = false;
  @Input() theme: 'white' | 'grey' = 'white';
  @Input() dbLeft = false;
  @Input() dbRight = false;

  @Input() override inputClassList = '';
  @Input() override labelClassList = '';
  @Input() override wrapClassList = '';

  @Output() iconEvent = new EventEmitter();

  viewPrivate = false;

  private isFocus = false;
  private value = '';
  private unsubscribe$ = new Subject();

  // eslint-disable-next-line @typescript-eslint/no-unused-vars,@typescript-eslint/no-empty-function,@typescript-eslint/explicit-module-boundary-types,@typescript-eslint/explicit-function-return-type
  onChange = (value: string) => {};

  // eslint-disable-next-line @typescript-eslint/explicit-module-boundary-types,@typescript-eslint/explicit-function-return-type,@typescript-eslint/no-empty-function
  onTouched = () => {};

  /**
   * Конструктор
   */
  constructor(private cdr: ChangeDetectorRef) {
    super();
  }

  /**
   * @inheritDoc
   */
  ngOnInit(): void {
    if (this.textZeroValue && this.shouldConvertToNegative) {
      throw new Error('Одновременно 2 режима ввода значений textZeroValue и shouldConvertToNegative запрещено!');
    }

    if (!this.id) {
      this.id = Element.generateId();
    }
    if (!this.name) {
      this.name = this.id;
    }
  }

  /**
   * Инициализация формы
   */
  private initFormControl(): void {
    if (!this.formControl) {
      console.error(`Не передан formControl в app-input (id: ${this.id}, name: ${this.name})`);

      return;
    }
    // Для асинхронных валидаторов
    this.formControl.statusChanges.pipe(distinctUntilChanged(), takeUntil(this.unsubscribe$)).subscribe((value) => {
      if (value === 'VALID' || value === 'INVALID') {
        this.cdr.detectChanges();
      }
    });

    // Переопределяем markAsTouched чтобы запустить обнаружение изменений
    const oldMarkAsTouched = this.formControl.markAsTouched.bind(this.formControl);
    this.formControl.markAsTouched = (opts?: { onlySelf?: boolean }): void => {
      oldMarkAsTouched(opts);
      this.cdr.markForCheck();
    };

    // Пустая строка => null
    this.formControl.valueChanges
      .pipe(
        filter((value) => value === '' || value === null),
        takeUntil(this.unsubscribe$),
      )
      .subscribe((value) => {
        if (value === '') {
          this.formControl.setValue(null);
        }
        this.cdr.detectChanges();
      });
  }

  /**
   * @inheritDoc
   */
  ngOnChanges(changes: SimpleChanges): void {
    if (changes.hasOwnProperty('formControl')) {
      this.initFormControl();
    }
  }

  /**
   * @inheritDoc
   */
  ngOnDestroy(): void {
    this.unsubscribe$.next(void 0);
    this.unsubscribe$.complete();
  }

  /**
   * @inheritDoc
   */
  writeValue(value: string): void {
    if (value !== this.value) {
      this.value = value;
      this.onChange(this.value);
      this.cdr.detectChanges();
    }
  }

  /**
   * @inheritDoc
   */
  registerOnChange(fn: (value: string) => void): void {
    this.onChange = fn;
  }

  /**
   * @inheritDoc
   */
  registerOnTouched(fn: () => void): void {
    this.onTouched = fn;
  }

  /**
   * @inheritDoc
   */
  setDisabledState(isDisabled: boolean): void {
    this.disabled = isDisabled;
  }

  /**
   * Возвращает тип ввода
   */
  getInputType(): string {
    if (this.private && !this.viewPrivate) {
      return 'password';
    }

    return 'text';
  }

  /**
   * Просмотр
   */
  view(): void {
    this.viewPrivate = !this.viewPrivate;
  }

  /**
   * Отчистит поле ввода
   */
  clear(): void {
    this.formControl.setValue(null);
  }

  /**
   * Обработчик фокуса
   */
  focusinHandler(): void {
    this.isFocus = true;
  }

  /**
   * Обработчик когда фокус снят
   */
  focusoutHandler(): void {
    this.isFocus = false;
  }

  /**
   * Возвращает цвет иконки
   */
  getIconClass(): string {
    return this.icon + this.getClassPrefix();
  }

  /**
   * Возвращает цвет текста инпута
   */
  getInputColorClass(): string {
    return 'input-color' + this.getClassPrefix();
  }

  /**
   * Возвращает префикс для класса
   */
  getClassPrefix(): string {
    return '-light-grey';
  }
}
